import '.ci{display:inline-flex;}.ssr-plugin'
import '.at{position:relative;}.ssr-plugin'
import '.bs{position:absolute;}.ssr-plugin'
import '.g4{width:max-content;}.ssr-plugin'
import '.g5{background-color:var(--brand-secondary);}.ssr-plugin'
import '.g6{padding:0.25rem 0.5rem;}.ssr-plugin'
import '.f5{border-radius:0.5rem;}.ssr-plugin'
import '.g7{z-index:50;}.ssr-plugin'
import '.dv{width:1rem;}.ssr-plugin'
import '.dw{height:1rem;}.ssr-plugin'
import '.g8{background-color:inherit;}.ssr-plugin'
import { ReactNode, useId, useLayoutEffect, useRef, useState } from "react"
import { Style, styled } from "inlines"
import { Text } from "../typography/Text"
import useMediaQuery from "@app/util/useMediaQuery"
import { ClientOnly } from "@app/components/blocks/clientOnly/ClientOnly"

const MIN_MARGIN_PX = 16

const getTooltipPositionStyle = (
    position: 'top' | 'right',
    arrowOffsetX: number,
) => {
    if (position === 'top') {
        return {
            bottom: '100%',
            left: '50%',
            transform: `translate(calc(-50% + ${arrowOffsetX}px), 0)`,
            marginBottom: '1rem',
        }
    }
    return {
        left: '100%',
        top: '50%',
        transform: 'translateY(-50%)',
        marginLeft: '1rem',
    }
}

const getArrowPositionStyle = (
    position: 'top' | 'right',
    arrowOffsetX: number,
) => {
    if (position === 'top') {
        return {
            top: '100%',
            left: `calc(50% - ${arrowOffsetX}px)`,
            transform: 'translate(-50%, -50%) rotate(45deg)',
        }
    }
    return {
        left: '-0.45rem',
        top: '50%',
        transform: 'translateY(-50%) rotate(45deg)',
    }
}

export type TooltipProps = {
  content: string
  children: ReactNode
  style?: Style
  containerStyle?: Style
  position?: 'top' | 'right'
}

function getClippingBounds(element: HTMLElement): { left: number; right: number } {
    let el: HTMLElement | null = element
    while (el) {
      const { overflow, overflowX, overflowY } = getComputedStyle(el)
      const clips =
        overflow === 'hidden' ||
        overflow === 'auto' ||
        overflow === 'scroll' ||
        overflowX === 'hidden' ||
        overflowX === 'auto' ||
        overflowX === 'scroll' ||
        overflowY === 'hidden' ||
        overflowY === 'auto' ||
        overflowY === 'scroll'
      if (clips) {
        const rect = el.getBoundingClientRect()
        return { left: rect.left, right: rect.right }
      }
      el = el.parentElement
    }
    return { left: 0, right: window.innerWidth }
}

const TooltipInternal = ({
    content,
    children,
    style,
    containerStyle,
    position = 'top',
}: TooltipProps) => {
    const id = useId()
    const isMobile = useMediaQuery()
    const [showTooltip, setShowTooltip] = useState(false)
    const [arrowOffsetX, setArrowOffsetX] = useState(0)
    const tooltipRef = useRef<HTMLDivElement | null>(null)

    useLayoutEffect(() => {
        if (!showTooltip || !tooltipRef.current) {return}

        const rect = tooltipRef.current.getBoundingClientRect()
        const { left: boundLeft, right: boundRight } = getClippingBounds(tooltipRef.current)

        let offset = 0
        if (rect.left < boundLeft + MIN_MARGIN_PX) {
            offset = boundLeft + MIN_MARGIN_PX - rect.left
        } else if (rect.right > boundRight - MIN_MARGIN_PX) {
            offset = boundRight - MIN_MARGIN_PX - rect.right
        }
        setArrowOffsetX(offset)
    }, [showTooltip])

    return (
        <styled.div className="ci at"
            style={{
                ...containerStyle,
            }}
            tabIndex={0}
            aria-describedby={showTooltip ? id : undefined}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => {
                setShowTooltip(false)
                setArrowOffsetX(0)
            }}
            onFocus={() => setShowTooltip(true)}
            onBlur={() => {
                setShowTooltip(false)
                setArrowOffsetX(0)
            }}
        >
            {children}
            {showTooltip && (
                <styled.div className="bs g4 g5 g6 f5 g7"
                    id={id}
                    role="tooltip"
                    ref={tooltipRef}
                    style={{
                        maxWidth: isMobile ? '50vw' : '30rem',
                        ...getTooltipPositionStyle(position, arrowOffsetX),
                        ...style,
                    }}
                >
                <styled.div className="bs dv dw g8"
                    style={{
                        ...getArrowPositionStyle(position, arrowOffsetX),
                    }}
                />
                <span style={{ position: 'relative', zIndex: 1 }}>
                    <Text.Small color="var(--palette-white)">{content}</Text.Small>
                </span>
            </styled.div>
        )}
    </styled.div>
  )
}

export const Tooltip = (props: TooltipProps) => {
    return (
        <ClientOnly fallback={props.children}>
            <TooltipInternal {...props} />
        </ClientOnly>
    )
}